import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function MedicalWebsiteDesignPage() {
  const features = [
    'Mobile-First Responsive Design',
    'HIPAA-Compliant Contact Forms',
    'Patient Portal Integration',
    'Online Appointment Booking',
    'Fast Loading Speed (Under 3 seconds)',
    'ADA Accessibility Compliance',
    'Secure SSL Encryption',
    'SEO-Optimized Architecture',
    'Content Management System',
    'Analytics & Tracking Setup',
    'HIPAA-Safe Chat Integration',
    'Review & Testimonial Showcase'
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your practice, target patients, competitors, and goals to create a custom website strategy.'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Our designers create mockups that reflect your brand while optimizing for patient conversion and trust.'
    },
    {
      step: '03',
      title: 'Development & Integration',
      description: 'We build your site with HIPAA-compliant forms, booking systems, and secure patient communication tools.'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'Rigorous testing across devices and browsers ensures everything works perfectly before going live.'
    },
    {
      step: '05',
      title: 'Training & Support',
      description: 'We train your team on managing content and provide ongoing support to keep your site performing optimally.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-user-heart-line',
      title: 'Patient-Focused Design',
      description: 'Every element is designed to build trust, answer questions, and guide patients to book appointments.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA Compliance Built-In',
      description: 'All forms, chat tools, and patient communications are implemented with HIPAA-aware security protocols.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile-First Experience',
      description: 'Over 70% of patients search on mobile. Your site will look perfect and convert on every device.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Lightning-Fast Performance',
      description: 'Fast loading speeds improve patient experience, SEO rankings, and conversion rates significantly.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'SEO-Optimized Structure',
      description: 'Built with clean code, proper schema markup, and SEO best practices to rank higher in search results.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Integrated Booking System',
      description: 'Patients can book appointments directly from your website 24/7, reducing phone calls and no-shows.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                MEDICAL WEBSITE DESIGN
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                HIPAA-Compliant Websites That Convert Visitors Into Patients
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Beautiful, conversion-optimized websites built specifically for healthcare providers. Every design element is crafted to build trust, educate patients, and drive appointment bookings while maintaining HIPAA compliance.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Custom Website Quote</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c573af62e68a21a5af865a4fe48c9bb0.jpg"
                  alt="Medical website design mockup"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY CHOOSE US
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Healthcare Websites That Actually Work
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We don't just build pretty websites. We create patient acquisition systems designed specifically for healthcare providers with compliance, conversion, and growth in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE FEATURES
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Everything Your Medical Practice Needs
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From HIPAA-compliant forms to integrated booking systems, we include all the features modern healthcare providers need to attract and convert patients online.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20website%20features%20showcase%20displaying%20mobile%20responsive%20design%20patient%20booking%20interface%20secure%20forms%20medical%20services%20pages%20and%20HIPAA%20compliance%20badges%20modern%20clean%20professional%20medical%20web%20design%20with%20purple%20gradient%20accents%20multiple%20device%20mockups&width=600&height=700&seq=webfeatures1&orientation=portrait"
                  alt="Website features showcase"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              From Strategy to Launch in 6-8 Weeks
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our proven process ensures your website launches on time, on budget, and ready to generate patient appointments from day one.
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-white">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Launch Your High-Converting Medical Website?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Schedule a free strategy call to discuss your practice goals, target patients, and how we can build a website that drives real growth.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Book Your Free Strategy Call</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>View Our Portfolio</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="medical-website-design" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
